export const THIS_YEAR = new Date().getFullYear();
export const THIS_MONTH = new Date().getMonth() + 1;
export const MONTHS_30_DAYS = [4, 6, 9, 11];
export const WEEK_DAYS: { [key: string]: string } = {
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
};

export const MONTHS: { [key: number]: string } = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};
// Number of weeks to display in the calendar
const CALENDAR_WEEKS = 6;

const zeroPad = (value: number, length = 2) => {
  return `${value}`.padStart(length, '0');
};

const isLeapYear = (year: number) => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

export const getMonthDays = (month: number, year: number) => {
  if (month < 1 || month > 12) {
    throw new RangeError(
      `The 'month' argument must be between 1 and 12 (inclusive). Actual: ${month}`
    );
  }

  if (year < 1970) {
    throw new RangeError(
      `The 'year' argument must be greater than 1970. Actual: ${year}`
    );
  }

  const leapYear = isLeapYear(year);
  return month === 2
    ? leapYear
      ? 29
      : 28
    : MONTHS_30_DAYS.includes(month)
    ? 30
    : 31;
};

export const getMonthFirstDay = (month: number, year: number) => {
  if (month < 1 || month > 12) {
    throw new RangeError(
      `The 'month' argument must be between 1 and 12 (inclusive). Actual: ${month}`
    );
  }

  if (year < 1970) {
    throw new RangeError(
      `The 'year' argument must be greater than 1970. Actual: ${year}`
    );
  }

  return new Date(`${year}-${zeroPad(month)}-01`).getDay() + 1;
};

export const isSameMonthAndYear = (date: Date, baseDate = new Date()) => {
  return (
    date.getMonth() === baseDate.getMonth() &&
    date.getFullYear() === baseDate.getFullYear()
  );
};

export const isSameDay = (date: Date, baseDate = new Date()) => {
  return (
    date.getDate() === baseDate.getDate() &&
    date.getMonth() === baseDate.getMonth() &&
    date.getFullYear() === baseDate.getFullYear()
  );
};

export const getDateWithoutTime = (date: Date) => {
  if (!date) return new Date();
  return new Date(date.toDateString());
};

// Returns date in ISO 8601 format i.e., yyyy-mm-dd
export const getDateISO = (date = new Date()) => {
  return [
    date.getFullYear(),
    zeroPad(date.getMonth() + 1),
    zeroPad(date.getDate()),
  ].join('/');
};

// Returns date in reverse ISO 8601 format i.e., dd-mm-yyyy
export const getDateISOReverse = (date = new Date()) => {
  return [
    zeroPad(date.getDate()),
    zeroPad(date.getMonth() + 1),
    date.getFullYear(),
  ].join('/');
};

// Gets the month and year before the given month and year
// For example: getPreviousMonth(1, 2000) => {month: 12, year: 1999}
// while: getPreviousMonth(12, 2000) => {month: 11, year: 2000}
export const getPreviousMonth = (month: number, year: number) => {
  const prevMonth = month > 1 ? month - 1 : 12;
  const prevMonthYear = month > 1 ? year : year - 1;
  return { month: prevMonth, year: prevMonthYear };
};

// Gets the month and year after the given month and year
// For example: getNextMonth(1, 2000) => {month: 2, year: 2000}
// while: getNextMonth(12, 2000) => {month: 1, year: 2001}
export const getNextMonth = (month: number, year: number) => {
  const nextMonth = month < 12 ? month + 1 : 1;
  const nextMonthYear = month < 12 ? year : year + 1;
  return { month: nextMonth, year: nextMonthYear };
};

// Calendar builder for a month in the specified year
// Returns an array of the calendar dates.
// Each calendar date is represented as an array => [YYYY, MM, DD]
export const calendar = (month = THIS_MONTH, year = THIS_YEAR) => {
  // Do some initial boundary checks
  if (month < 1 || month > 12) {
    throw new RangeError(
      `The 'month' argument must be between 1 and 12 (inclusive). Actual: ${month}`
    );
  }

  if (year < 1970) {
    throw new RangeError(
      `The 'year' argument must be greater than 1970. Actual: ${year}`
    );
  }

  // Get number of days in the month and the month's first day
  const monthDays = getMonthDays(month, year);
  const monthFirstDay = getMonthFirstDay(month, year);

  // Get number of days to be displayed from previous and next months
  // These ensure a total of 42 days (6 weeks) displayed on the calendar
  const daysFromPrevMonth = monthFirstDay - 1;
  const daysFromNextMonth =
    CALENDAR_WEEKS * 7 - (daysFromPrevMonth + monthDays);

  // Get the previous and next months and years
  const { month: prevMonth, year: prevMonthYear } = getPreviousMonth(
    month,
    year
  );
  const { month: nextMonth, year: nextMonthYear } = getNextMonth(month, year);

  // Get number of days in previous month
  const prevMonthDays = getMonthDays(prevMonth, prevMonthYear);

  // Builds dates to be displayed from previous month
  const prevMonthDates = [...new Array(daysFromPrevMonth)].map((_, index) => {
    const day = index + 1 + (prevMonthDays - daysFromPrevMonth);
    return [prevMonthYear, zeroPad(prevMonth), zeroPad(day)];
  });

  // Builds dates to be displayed from current month
  const thisMonthDates = [...new Array(monthDays)].map((_, index) => {
    const day = index + 1;
    return [year, zeroPad(month), zeroPad(day)];
  });

  // Builds dates to be displayed from next month
  const nextMonthDates = [...new Array(daysFromNextMonth)].map((_, index) => {
    const day = index + 1;
    return [nextMonthYear, zeroPad(nextMonth), zeroPad(day)];
  });

  // Combines all dates from previous, current and next months
  return [...prevMonthDates, ...thisMonthDates, ...nextMonthDates];
};

export const isValidDateString = (dateString: string) => {
  const numArr = dateString?.split('/');
  if (
    /^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[012])[\/\-]\d{4}$/.test(
      dateString
    ) &&
    numArr.length === 3 &&
    dateString.length === 10
  ) {
    return true;
  } else {
    return false;
  }
};

export default calendar;
